// Логиката на справките „Продадени продукти" / „Продадени рецепти": търсене,
// филтрите от–до в колоните (Бруто; при рецепти и Продадено — бройките),
// сортиране по колона и сумите. Бекендът връща редовете по име — без избрано
// сортиране подредбата остава неговата. „Последна продажба" (само рецепти)
// сортира по дата, а редовете без нея са последни и в двете посоки. Реалното и разликата при продуктите
// (колоните „Разнесено" и „Разлика") се смятат в `../sold/soldVarianceModel.ts`;
// сортирането по тях е тук, защото е едно за цялата таблица.
import type { SoldSellable } from "../../../../api/inventory";
import {
  EMPTY_RANGE,
  type FilterSpecs,
  type NumberRange,
} from "../../components/filters/columnFilterModel";
import type { SoldRow } from "../sold/soldVarianceModel";
import {
  compareText,
  matchesWords,
  searchWords,
  sortRows,
  type ReportSort,
} from "./reportListModel";

// Ред, който справката сортира: теоретичното + (при продуктите) реалното
type SortableSold = SoldSellable & Partial<Pick<SoldRow, "actual" | "variance">>;

// Количеството на продуктите е в различни мерни единици (кг, л, бр) — диапазон
// по него няма смисъл; при рецептите то е брой продадени и филтърът е полезен.
export type SoldFilters = { qty: NumberRange; gross: NumberRange };
export type SoldFilterKey = keyof SoldFilters;
export const EMPTY_SOLD_FILTERS: SoldFilters = { qty: EMPTY_RANGE, gross: EMPTY_RANGE };

export const soldFilterSpecs = <T extends SoldSellable = SoldSellable>({
  titles,
  formatMoney,
  formatCount,
}: {
  titles: Record<SoldFilterKey, string>;
  formatMoney: (value: number) => string;
  formatCount: (value: number) => string;
}): FilterSpecs<T, SoldFilters> => ({
  qty: { kind: "range", title: titles.qty, valueOf: (row) => row.quantity || 0, format: formatCount },
  gross: { kind: "range", title: titles.gross, valueOf: (row) => row.totalSum || 0, format: formatMoney },
});

// actualQty/actualGross — „Разнесено", diffQty/diffGross — „Разлика" (само продукти);
// lastSold — „Последна продажба" (само рецепти)
export type SoldSortKey =
  | "name"
  | "ingredients"
  | "qty"
  | "lastSold"
  | "avgPrice"
  | "net"
  | "gross"
  | "actualQty"
  | "actualGross"
  | "diffQty"
  | "diffGross";
export type SoldSort = ReportSort<SoldSortKey>;

export const soldSortFirstDir = (key: SoldSortKey) => (key === "name" ? "asc" : "desc");

// Рецептите се броят в бройки (без мерна единица).
export const formatCount = (value: number) =>
  Number(value ?? 0).toLocaleString("bg-BG", { maximumFractionDigits: 3 });

// Милисекундите на последната продажба; null — няма (стар BE или продукт)
export const lastSoldTime = (row: SoldSellable): number | null => {
  if (!row.lastSoldAt) return null;
  const time = Date.parse(row.lastSoldAt);
  return Number.isNaN(time) ? null : time;
};

const compareSold = (a: SortableSold, b: SortableSold, key: SoldSortKey) => {
  switch (key) {
    case "name":
      return compareText(a.name, b.name);
    case "ingredients":
      return a.ingredientsCount - b.ingredientsCount;
    case "qty":
      return a.quantity - b.quantity;
    // Редовете без дата не стигат дотук — `sortSold` ги слага последни
    case "lastSold":
      return (lastSoldTime(a) ?? 0) - (lastSoldTime(b) ?? 0);
    case "avgPrice":
      return a.avgPrice - b.avgPrice;
    case "net":
      return a.sumNoVat - b.sumNoVat;
    case "gross":
      return a.totalSum - b.totalSum;
    // Без реално (рецепти или още се зарежда) редът се сортира като 0
    case "actualQty":
      return (a.actual?.quantity ?? 0) - (b.actual?.quantity ?? 0);
    case "actualGross":
      return (a.actual?.valueWithVat ?? 0) - (b.actual?.valueWithVat ?? 0);
    case "diffQty":
      return (a.variance?.quantity ?? 0) - (b.variance?.quantity ?? 0);
    case "diffGross":
      return (a.variance?.valueWithVat ?? 0) - (b.variance?.valueWithVat ?? 0);
  }
};

// Търсачката над таблицата (по име) — базата за филтрите в колоните
export const searchSold = <T extends SoldSellable>(rows: T[], query: string) => {
  const words = searchWords(query);
  return words.length ? rows.filter((row) => matchesWords([row.name], words)) : rows;
};

export const sortSold = <T extends SortableSold>(rows: T[], sort: SoldSort | null): T[] => {
  if (sort?.key !== "lastSold") return sortRows(rows, sort, compareSold);
  // Без дата — най-долу, каквато и да е посоката
  const dated = rows.filter((row) => lastSoldTime(row) != null);
  const undated = rows.filter((row) => lastSoldTime(row) == null);
  return [...sortRows(dated, sort, compareSold), ...undated];
};

// Търсене + сортиране наведнъж (без филтрите в колоните)
export const filterSold = <T extends SortableSold>(rows: T[], query: string, sort: SoldSort | null) =>
  sortSold(searchSold(rows, query), sort);

export const soldTotals = (rows: SoldSellable[]) => ({
  count: rows.length,
  qty: rows.reduce((sum, row) => sum + (row.quantity || 0), 0),
  net: rows.reduce((sum, row) => sum + (row.sumNoVat || 0), 0),
  gross: rows.reduce((sum, row) => sum + (row.totalSum || 0), 0),
});
